import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { LeagueGame, Team } from '../../../sdk/model';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  MenuItem,
  Select,
  Typography,
  useTheme,
  useMediaQuery,
  CardHeader,
  Avatar,
  CardActions,
  Button,
  Modal,
} from '@mui/material';
import {
  SportsFootball as SportsFootballIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  SportsScore as SportsScoreIcon,
  ArrowForward as ArrowForwardIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import EditScore from '../games/EditScore';

interface AllTeamsScheduleProps {
  leagueId: string;
  gamesByWeek: { [weekNumber: string]: LeagueGame[] };
  teams: Team[];
  editMode: boolean;
  editSchedule: ((gameId: string, homeAway: 'home' | 'away', teamId: string) => void) | null;
  canEditScores: boolean;
}

const AllTeamsSchedule: React.FC<AllTeamsScheduleProps> = ({
  leagueId,
  gamesByWeek,
  teams,
  editMode,
  editSchedule,
  canEditScores
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = React.useState<{ [gameId: string]: boolean }>({});
  const [editingScore, setEditingScore] = React.useState<LeagueGame | undefined>(undefined);

  const handleTeamChange = async (
    gameId: string,
    homeAway: 'home' | 'away',
    teamId: string
  ) => {
    if (!editSchedule) return;
    
    try {
      setLoading(prev => ({ ...prev, [gameId]: true }));
      await editSchedule(gameId, homeAway, teamId);
    } finally {
      setLoading(prev => ({ ...prev, [gameId]: false }));
    }
  };

  const navigateToMatchup = (gameId: string) => {
    navigate(`/league/${leagueId}/matchup/id/${gameId}`);
  };

  const findClosestGame = (games: LeagueGame[]) => {
    if (games.length <= 1) return null;
    
    let closestGame = null;
    let smallestDiff = Infinity;
    
    games.forEach(game => {
      if (game.homeTeamScore !== undefined && game.awayTeamScore !== undefined) {
        const diff = Math.abs(game.homeTeamScore - game.awayTeamScore);
        if (diff < smallestDiff) {
          smallestDiff = diff;
          closestGame = game.id;
        }
      }
    });
    
    return closestGame;
  };

  const findHighestScore = (games: LeagueGame[]) => {
    let highestScore = -Infinity;
    let highestScoringGame = null;
    
    games.forEach(game => {
      if (game.homeTeamScore !== undefined && game.homeTeamScore > highestScore && game.homeTeamScore > 0) {
        highestScore = game.homeTeamScore;
        highestScoringGame = game.id;
      }
      if (game.awayTeamScore !== undefined && game.awayTeamScore > highestScore && game.awayTeamScore > 0) {
        highestScore = game.awayTeamScore;
        highestScoringGame = game.id;
      }
    });
    
    return highestScore > 0 ? highestScoringGame : null;
  };

  const isHighestScoringTeam = (game: LeagueGame, teamType: 'home' | 'away') => {
    const weeklyGames = gamesByWeek[game.weekNumber?.toString() || ''] || [];
    const highestScoringGame = findHighestScore(weeklyGames);
    if (!highestScoringGame) return false;
    
    return game.id === highestScoringGame && 
           ((teamType === 'home' && game.homeTeamScore !== undefined && 
             game.homeTeamScore > (game.awayTeamScore || -Infinity)) ||
            (teamType === 'away' && game.awayTeamScore !== undefined && 
             game.awayTeamScore > (game.homeTeamScore || -Infinity)));
  };

  const renderTeamSelect = (game: LeagueGame, teams: Team[], teamType: 'home' | 'away') => {
    const teamId = teamType === 'home' ? game.homeTeamId : game.awayTeamId;
    const team = teams.find(t => t.id === teamId);
    
    return (
      <Select
        value={teamId || ''}
        onChange={(e) => handleTeamChange(game.id!, teamType, e.target.value as string)}
        size="small"
        fullWidth
        disabled={loading[game.id!]}
        sx={{ 
          my: 0.5,
          '& .MuiSelect-select': {
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }
        }}
      >
        <MenuItem value="">
          <em>Select Team</em>
        </MenuItem>
        {teams.map((team) => (
          <MenuItem key={team.id} value={team.id}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {team.picture ? (
                <Avatar src={team.picture} sx={{ width: 24, height: 24 }} />
              ) : (
                <Avatar sx={{ width: 24, height: 24, bgcolor: 'primary.main' }}>
                  <SportsFootballIcon fontSize="small" />
                </Avatar>
              )}
              {team.name}
            </Box>
          </MenuItem>
        ))}
      </Select>
    );
  };

  const renderTeamRow = (game: LeagueGame, teams: Team[], teamType: 'home' | 'away') => {
    const teamId = teamType === 'home' ? game.homeTeamId : game.awayTeamId;
    const team = teams.find(t => t.id === teamId);
    const isHome = teamType === 'home';
    const teamName = team?.name;
    const teamScore = isHome ? game.homeTeamScore : game.awayTeamScore;
    const opponentScore = isHome ? game.awayTeamScore : game.homeTeamScore;
    const isWinner = game.finalScore && teamScore !== undefined && opponentScore !== undefined && teamScore > opponentScore;
    const isLoser = game.finalScore && teamScore !== undefined && opponentScore !== undefined && teamScore < opponentScore;
    const isHighestScorer = game.finalScore && isHighestScoringTeam(game, teamType);

    if (editMode && editSchedule) {
      return renderTeamSelect(game, teams, teamType);
    }

    return (
      <Box
        key={`${game.id}-${teamType}`}
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 1.5,
          borderRadius: 1,
          bgcolor: isWinner ? 'rgba(46, 125, 50, 0.08)' : isLoser ? 'rgba(211, 47, 47, 0.08)' : 'transparent',
          transition: 'all 0.2s ease',
          mb: 1,
          border: `1px solid ${theme.palette.divider}`,
          position: 'relative',
        }}
      >
        {isHighestScorer && (
          <Box 
            component="span" 
            role="img" 
            aria-label="Highest scoring team this week"
            sx={{
              position: 'absolute',
              top: 4,
              left: 4,
              fontSize: '1.2rem',
              lineHeight: 1,
            }}
          >
            👑
          </Box>
        )}
        {team?.picture ? (
          <Avatar src={team.picture} sx={{ width: 36, height: 36, mr: 1.5 }} />
        ) : (
          <Avatar sx={{ width: 36, height: 36, mr: 1.5, bgcolor: 'primary.main' }}>
            <SportsFootballIcon fontSize="small" />
          </Avatar>
        )}
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight={500}>
            {teamName || 'TBD'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {teams.find(t => t.id === teamId)?.owners?.map(owner => owner.username).join(', ') || 'Available'}
          </Typography>
        </Box>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          minWidth: 40,
          justifyContent: 'flex-end'
        }}>
          {game.finalScore ? (
            <>
              <Typography 
                variant="h6" 
                fontWeight={700}
                color={isWinner ? 'success.main' : isLoser ? 'error.main' : 'text.primary'}
              >
                {teamScore !== undefined ? teamScore : '-'}
              </Typography>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {teamScore !== undefined ? teamScore : 'TBD'}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  const sortedWeeks = Object.keys(gamesByWeek).sort(
    (a, b) => parseInt(a, 10) - parseInt(b, 10)
  );

  if (sortedWeeks.length === 0) {
    return (
      <Box textAlign="center" py={4}>
        <Typography variant="body1" color="text.secondary">
          No schedule data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 1, sm: 2 }, py: 2 }}>
      <Modal open={editingScore} onClose={() => setEditingScore(undefined)} 
          aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description"
          sx={{margin: '5%'}}>
        <EditScore game={editingScore!} teams={teams} onClose={() => setEditingScore(undefined)} />
      </Modal>
      {sortedWeeks.map((weekNumber) => {
        const weeklyGames = gamesByWeek[weekNumber];
        const postseasonRoundNumber = weeklyGames[0]?.postseasonRoundNumber;
        const weekTitle = postseasonRoundNumber
          ? `Playoffs Round ${postseasonRoundNumber}, Week ${weekNumber}`
          : `Week ${weekNumber}`;

        return (
          <Box key={weekNumber} mb={4}>
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3,
              p: 2,
              borderRadius: 1,
              bgcolor: 'background.paper',
              boxShadow: 1
            }}>
              <Typography
                variant="h6"
                component="h2"
                sx={{
                  fontSize: { xs: '1.2rem', sm: '1.4rem' },
                  fontWeight: 600,
                  color: 'primary.main'
                }}
              >
                {weekTitle}
              </Typography>
              
              {editMode && (
                <Select
                  value=""
                  displayEmpty
                  size="small"
                  sx={{ 
                    minWidth: 160,
                    '& .MuiSelect-select': { py: 0.75, fontSize: '0.875rem' }
                  }}
                  renderValue={() => "Week Actions"}
                >
                  <MenuItem value="">
                    <em>Edit Week</em>
                  </MenuItem>
                  <MenuItem value="edit">Edit All Games</MenuItem>
                  <MenuItem value="add">Add New Game</MenuItem>
                </Select>
              )}
            </Box>

            <Grid container spacing={3}>
              {weeklyGames.map((game, index) => {
                const isClosestGame = findClosestGame(weeklyGames) === game.id;
                return (
                  <Grid item xs={12} sm={6} lg={4} key={game.id}>
                    <Card 
                      elevation={2}
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          transform: editMode ? 'none' : 'translateY(-4px)',
                          boxShadow: editMode ? 2 : 4,
                          cursor: editMode ? 'default' : 'pointer',
                        },
                      }}
                      onClick={!editMode ? () => navigateToMatchup(game.id!) : undefined}
                    >
                      <CardHeader
                        title={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="subtitle1" fontWeight={500}>
                              {`Matchup ${index + 1}`}{game.playoff ? " - PLAYOFF" : ""}
                            </Typography>
                            <Chip 
                              label={game.finalScore ? 'FINAL' : 'UPCOMING'}
                              size="small"
                              color={game.finalScore ? 'success' : 'default'}
                              sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                              icon={game.finalScore ? <SportsScoreIcon fontSize="small" /> : undefined}
                            />
                          </Box>
                        }
                        subheader={
                          <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                            {game.location && (
                              <>
                                <LocationIcon fontSize="small" sx={{ mr: 0.5, fontSize: '1rem', opacity: 0.7 }} />
                                <Typography variant="caption" color="text.secondary">
                                  {game.location}
                                </Typography>
                              </>
                            )}
                          </Box>
                        }
                        sx={{ pb: 1 }}
                      />
                      
                      <CardContent sx={{ flexGrow: 1, pt: 0 }}>
                        {renderTeamRow(game, teams, 'away')}
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'center',
                          my: 0.5,
                          color: 'text.secondary',
                          fontSize: '0.75rem',
                          fontWeight: 600
                        }}>
                          VS
                        </Box>
                        {renderTeamRow(game, teams, 'home')}
                      </CardContent>

                      <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
                        {canEditScores &&
                          <Button 
                            size="small" 
                            endIcon={<EditIcon />}
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingScore(game);
                            }}
                          >
                              Edit Score
                          </Button>
                        }
                        <Button 
                          size="small" 
                          endIcon={<ArrowForwardIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigateToMatchup(game.id!);
                          }}
                        >
                            View Details
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        );
      })}
    </Box>
  );
};

export default AllTeamsSchedule;